import { useEffect, useMemo, useState } from 'react';
import type { SessionUser } from '../../../utils/session';
import { getSessionUser } from '../../../utils/session';

interface RefillUser {
  Name: string;
  LinkToken: string;
}

const BLOCKED_CLASSES = [
  'BLOCKED_BY_PATIENT',
  'PATIENT_NEEDS_ACCOUNT',
  'NEEDS_PATIENT_AUTHORIZATION',
];

interface Dependent {
  DependantClass?: string;
  FirstName?: string;
  LastName?: string;
  LinkToken?: string;
  CanRefillScripts?: boolean;
}

function buildRefillUsers(user: SessionUser | null): RefillUser[] {
  if (!user) return [];

  const primary: RefillUser = {
    Name: `${((user.FirstName as string) || '').toUpperCase()} ${((user.LastName as string) || '').toUpperCase()}`.trim(),
    LinkToken: (user.LinkToken as string) || '',
  };

  const dependents = (Array.isArray(user.Dependants) ? user.Dependants : []) as Dependent[];
  const eligible = dependents
    .filter(d => {
      if (!d.DependantClass || BLOCKED_CLASSES.includes(d.DependantClass)) return false;
      if (d.CanRefillScripts === false) return false;
      return true;
    })
    .map(
      (d): RefillUser => ({
        Name: `${(d.FirstName || '').toUpperCase()} ${(d.LastName || '').toUpperCase()}`.trim(),
        LinkToken: d.LinkToken || '',
      })
    );

  return [primary, ...eligible];
}

export interface RefillsHeaderProps {
  currentLinkToken: string;
  onUserChange: (linkToken: string) => void;
  view: 'List' | 'Grid';
  onViewChange: (view: 'List' | 'Grid') => void;
  loading: boolean;
}

export function RefillsHeader({
  currentLinkToken,
  onUserChange,
  view,
  onViewChange,
  loading,
}: RefillsHeaderProps) {
  const user = useMemo(() => getSessionUser<SessionUser>(), []);
  const refillUsers = useMemo(() => buildRefillUsers(user), [user]);
  const [selected, setSelected] = useState(currentLinkToken || refillUsers[0]?.LinkToken || '');

  useEffect(() => {
    if (currentLinkToken && currentLinkToken !== selected) {
      setSelected(currentLinkToken);
    }
  }, [currentLinkToken, selected]);

  const handleUserChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const linkToken = e.target.value;
    setSelected(linkToken);
    onUserChange(linkToken);
  };

  return (
    <ul className="list-group refill-sidebar-listgroup">
      <li className="list-group-item">
        <strong>Refill Prescriptions For:</strong>
        <select
          className="report-user form-control custom"
          value={selected}
          onChange={handleUserChange}
          disabled={loading}
          required
        >
          {refillUsers.map(u => (
            <option key={u.LinkToken} value={u.LinkToken}>
              {u.Name}
            </option>
          ))}
        </select>
      </li>
      <li className="list-group-item">
        <strong>View: </strong>
        <br />
        <ul className="list-unstyled">
          <li>
            <label style={{ marginRight: 8 }}>
              <input
                type="radio"
                name="view"
                value="List"
                checked={view === 'List'}
                onChange={() => onViewChange('List')}
              />{' '}
              List
            </label>
            <label>
              <input
                type="radio"
                name="view"
                value="Grid"
                checked={view === 'Grid'}
                onChange={() => onViewChange('Grid')}
              />{' '}
              Grid
            </label>
          </li>
        </ul>
      </li>
    </ul>
  );
}
